import { pick } from '../i18n.js';

const zh = {
  readFailed: '资料读取失败，请重试。',
  place: '放到画布',
  download: '下载原文件 ↓',
  fullView: '全屏阅读',
  exitFullView: '退出全屏',
  viewSource: '查看源码',
  saveNode: '保存为参数节点',
  loading: '正在读取…',
  parameterHint: '放到画布后，可在卡片上调整参数并运行。',
  fileTooLarge: '文件过大，请下载查看。',
  unreadable: '内容暂不可读取。',
  nextPage: '下一页 ›',
  previousPage: '‹ 上一页',
  loadingPreview: '正在载入预览…',
  noPreview: '此格式尚无预览，可下载已有文件。',
  previewFailed: '预览暂不可用。',
  modelTooLarge: '模型超过 32 MiB 预览上限，可下载原文件。',
  metrics: '技术指标',
  noDomainMetrics: 'GFA、楼层等领域指标尚未提供。',
  geometry: '几何数量与尺寸',
  embeddedMetrics: '模型内嵌指标',
  invalidEmbeddedMetrics: '模型内嵌指标格式无效。',
  metricsTooLarge: '指标文档超过大小限制。',
  invalidLinkedMetrics: '关联指标文档不可读取或格式无效。',
  inKnowledge: '当前版本已加入项目知识库',
  addKnowledge: '加入项目知识库',
  knowledgeQueued: '入库已排队，可以继续建模',
  knowledgeIndexing: '正在建立检索索引，可以继续建模',
  knowledgeFailed: '失败',
  knowledgePending: '尚未完成',
  knowledgeIncomplete: '入库未完成',
  removeKnowledge: '移出知识库',
  knowledgeRemoved: '已移出项目知识库，原文件保留',
  removeIncomplete: '移出未完成',
  codeTooLarge: '代码超过参数模板大小上限。',
  codeTemplate: '代码模板',
  number: '数值',
  integer: '整数',
  boolean: '开关',
  string: '文字',
  enum: '枚举',
  commaSeparated: '用逗号分隔',
  parameterName: '参数名',
  displayName: '显示名称',
  type: '类型',
  defaultValue: '默认值',
  unit: '单位',
  minimum: '最小值',
  maximum: '最大值',
  step: '步长',
  enumOptions: '枚举选项',
  insertMark: '插入参数标记',
  createNode: '创建参数节点',
  projectChanged: '项目已切换。',
  createFailed: '参数程序创建失败',
  name: '名称',
  environment: '执行环境',
  declareHint: '声明可调参数，将代码中的对应表达式替换为参数标记。标记不能放在引号或注释内。',
  addParameter: '添加参数',
  codeUnreadable: '代码暂不可读取。',
  contentHash: (hash: string) => `内容标识 ${hash}`,
  pageAlt: (name: string, page: number) => `${name} · 第 ${page} 页`,
  pageLabel: (page: number, total: number) => `第 ${page} / ${total} 页`,
  metricCount: (n: number) => `${n} 项`,
  metricSource: (label: string, sourceVersion: number, modelVersion: number) => `来源：${label} · v${sourceVersion} · 模型 v${modelVersion}`,
  indexed: (n: number, version: number) => `已入库 · ${n} 个文本块 · 源版本 v${version}`,
  indexState: (failed: boolean, code?: string) => `入库${failed ? zh.knowledgeFailed : zh.knowledgePending}${code ? '：' + code : ''}`,
  parameterNameLabel: (n: number) => `参数名 ${n}`,
  parameterLabel: (n: number) => `参数 ${n}`,
  parameterDisplayLabel: (n: number) => `参数标签 ${n}`,
  parameterTypeLabel: (n: number) => `参数类型 ${n}`,
  defaultValueLabel: (n: number) => `默认值 ${n}`,
  unitLabel: (n: number) => `单位 ${n}`,
  minimumLabel: (n: number) => `最小值 ${n}`,
  maximumLabel: (n: number) => `最大值 ${n}`,
  stepLabel: (n: number) => `步长 ${n}`,
  enumOptionsLabel: (n: number) => `枚举选项 ${n}`,
  rhinoCsharp: 'Rhino C#',
  grasshopperCsharp: 'Grasshopper C#',
  megabytes: (size: string) => `${size} MB`,
  kilobytes: (size: number) => `${size} KB`,
  bytes: (size: number) => `${size} B`,
  metadata: (kind: string, version: number, mediaType: string, size: string) => `${kind} · v${version} · ${mediaType} · ${size}`,
};

const en: typeof zh = {
  readFailed: 'Could not read the file. Try again.',
  place: 'Place on canvas',
  download: 'Download original file ↓',
  fullView: 'Full screen',
  exitFullView: 'Exit full screen',
  viewSource: 'View source',
  saveNode: 'Save as parameter node',
  loading: 'Loading…',
  parameterHint: 'Place on the canvas to adjust parameters and run from the card.',
  fileTooLarge: 'This file is too large to preview. Download it to view.',
  unreadable: 'Content is temporarily unavailable.',
  nextPage: 'Next page ›',
  previousPage: '‹ Previous page',
  loadingPreview: 'Loading preview…',
  noPreview: 'No preview is available for this format. You can download the file.',
  previewFailed: 'Preview is temporarily unavailable.',
  modelTooLarge: 'This model exceeds the 32 MiB preview limit. Download the original file.',
  metrics: 'Technical metrics',
  noDomainMetrics: 'Domain metrics such as GFA and floor count are not available yet.',
  geometry: 'Geometry counts and dimensions',
  embeddedMetrics: 'Embedded model metrics',
  invalidEmbeddedMetrics: 'Embedded model metrics have an invalid format.',
  metricsTooLarge: 'The metrics document exceeds the size limit.',
  invalidLinkedMetrics: 'The linked metrics document is unreadable or has an invalid format.',
  inKnowledge: 'This version is in the project knowledge base',
  addKnowledge: 'Add to project knowledge base',
  knowledgeQueued: 'Indexing is queued. You can keep modeling',
  knowledgeIndexing: 'Building the search index. You can keep modeling',
  knowledgeFailed: 'failed',
  knowledgePending: 'is not complete',
  knowledgeIncomplete: 'Indexing did not complete',
  removeKnowledge: 'Remove from knowledge base',
  knowledgeRemoved: 'Removed from the project knowledge base. The original file is kept',
  removeIncomplete: 'Removal did not complete',
  codeTooLarge: 'The code exceeds the parameter template size limit.',
  codeTemplate: 'Code template',
  number: 'Number',
  integer: 'Integer',
  boolean: 'Toggle',
  string: 'Text',
  enum: 'Enum',
  commaSeparated: 'Separate with commas',
  parameterName: 'Parameter name',
  displayName: 'Display name',
  type: 'Type',
  defaultValue: 'Default value',
  unit: 'Unit',
  minimum: 'Minimum',
  maximum: 'Maximum',
  step: 'Step',
  enumOptions: 'Enum options',
  insertMark: 'Insert parameter marker',
  createNode: 'Create parameter node',
  projectChanged: 'The project has changed.',
  createFailed: 'Could not create the parameter program',
  name: 'Name',
  environment: 'Execution environment',
  declareHint: 'Declare adjustable parameters and replace their code expressions with parameter markers. Keep markers outside quotes and comments.',
  addParameter: 'Add parameter',
  codeUnreadable: 'Code is temporarily unavailable.',
  contentHash: (hash: string) => `Content ID ${hash}`,
  pageAlt: (name: string, page: number) => `${name} · Page ${page}`,
  pageLabel: (page: number, total: number) => `Page ${page} / ${total}`,
  metricCount: (n: number) => `${n} ${n === 1 ? 'item' : 'items'}`,
  metricSource: (label: string, sourceVersion: number, modelVersion: number) => `Source: ${label} · v${sourceVersion} · Model v${modelVersion}`,
  indexed: (n: number, version: number) => `Indexed · ${n} text ${n === 1 ? 'chunk' : 'chunks'} · Source v${version}`,
  indexState: (failed: boolean, code?: string) => `Indexing ${failed ? en.knowledgeFailed : en.knowledgePending}${code ? ': ' + code : ''}`,
  parameterNameLabel: (n: number) => `Parameter name ${n}`,
  parameterLabel: (n: number) => `Parameter ${n}`,
  parameterDisplayLabel: (n: number) => `Parameter label ${n}`,
  parameterTypeLabel: (n: number) => `Parameter type ${n}`,
  defaultValueLabel: (n: number) => `Default value ${n}`,
  unitLabel: (n: number) => `Unit ${n}`,
  minimumLabel: (n: number) => `Minimum ${n}`,
  maximumLabel: (n: number) => `Maximum ${n}`,
  stepLabel: (n: number) => `Step ${n}`,
  enumOptionsLabel: (n: number) => `Enum options ${n}`,
  rhinoCsharp: 'Rhino C#',
  grasshopperCsharp: 'Grasshopper C#',
  megabytes: (size: string) => `${size} MB`,
  kilobytes: (size: number) => `${size} KB`,
  bytes: (size: number) => `${size} B`,
  metadata: (kind: string, version: number, mediaType: string, size: string) => `${kind} · v${version} · ${mediaType} · ${size}`,
};

export const artifactViewerTextByLocale = { zh, en };
export const artifactViewerText = pick<typeof zh>({ zh, en });
